import React, { useEffect, useCallback, useState, useMemo } from 'react';
import { Grid, Typography, TextField, Button } from '@material-ui/core';
import { withStyles } from '@material-ui/styles';
import AddIcon from '@material-ui/icons/Add';
import { LineMaterialSchema } from '../../schemas';
import btnStyles from '../../styles';
import { v4 as uuidv4 } from 'uuid';
import SpecificationChip, { getItemId } from '../SpecificationChip/SpecificationChip';
import { MEASUREMENT_UNITS, MEASURE_UNITS_BY_SYSTEM } from 'common/measurements';
import { calculateLine, round, resolveDecimalPlaces } from 'helpers/material-calculations';
import { SortableList } from 'lib';

const LineSpecifications = ({
  decimalPlaces,
  values,
  remove,
  push,
  replace,
  move,
  type,
  classes,
  data,
  setIsCorrectRequest,
  measurement,
}) => {
  const [editIndex, setEditIndex] = useState(null);
  const [id, setId] = useState('');
  const [width, setWidth] = useState('');
  const [ratio, setRatio] = useState('');
  const [testQuantity, setTestQuantity] = useState('');
  const [lineResult, setLineResult] = useState('');
  const [errors, setErrors] = useState({});

  const validatedValues = useMemo(() => {
    return values.map((line, index) => ({
      ...line,
      isValid: LineMaterialSchema(values.filter((line, i) => i !== index), null, type)
        .isValidSync(line, { stripUnknown: true })
    }))
  }, [values, type])

  const clearErrors = useCallback(
    (prop) => {
      if (!prop) {
        setErrors({});
      } else {
        const errorsObject = Object.assign({}, errors);
        delete errorsObject[prop];
        setErrors(errorsObject);
      }
    },
    [errors]
  );

  const resetForm = () => {
    setWidth('');
    setRatio('');
    setTestQuantity('');
    setLineResult('');
    setId('');
    clearErrors();
  };

  const addLine = useCallback(() => {
    try {
      const result = {
        width,
        ratio,
        testQuantity,
        _id: uuidv4(),
      };

      const validated = LineMaterialSchema(values || [], null, type).validateSync(result, {
        abortEarly: false,
        stripUnknown: true,
      });

      push(validated);
      setIsCorrectRequest(true);
      resetForm();
    } catch (err) {
      const errObject = {};
      (err.inner || []).forEach(({ path, message }) => (errObject[path] = message));
      setErrors(errObject);
    }
  }, [push, width, ratio, testQuantity, values, id]);

  const editSave = () => {
    try {
      const result = {
        width,
        ratio,
        _id: id,
      };

      const validated = LineMaterialSchema(values || [], editIndex).validateSync(result, {
        abortEarly: false,
        stripUnknown: true,
      });

      replace(editIndex, validated);

      setIsCorrectRequest(true);
      resetForm();
      setEditIndex(null);
    } catch (err) {
      const errObject = {};
      (err.inner || []).forEach(({ path, message }) => (errObject[path] = message));
      setErrors(errObject);
    }
  };

  useEffect(() => {
    if (Number(testQuantity) > 0) {
      const dp = resolveDecimalPlaces(decimalPlaces);
      const convertRatio = measurement === 'imperial' ? 12 : 100;
      const widthFactor = round(width / convertRatio, dp);
      if (type === 'eradication') setLineResult(`${round(testQuantity * widthFactor, dp)}`)
      else if (Number(ratio) > 0) setLineResult(`${calculateLine(testQuantity, ratio, dp)}`);
      else setLineResult('');

    } else {
      setLineResult('');
    }
  }, [type, ratio, testQuantity, width, decimalPlaces]);

  return (
    <Grid container xs={12} style={{ borderTop: '1px solid #D9DAE3', marginBottom: '15px' }}>
      <Grid item xs={8} md={'auto'} style={{ marginBottom: '17px', marginTop: '10px' }}>
        <Typography variant="h5">Line Specifications:</Typography>
      </Grid>

      <Grid container spacing={1} style={{ justifyContent: 'flex-start' }}>
        <Grid item xs={6} lg={3}>
          <label htmlFor="lineWidth">
            <Typography variant="body1" color="textSecondary">
              {`Available Width (${MEASURE_UNITS_BY_SYSTEM[measurement][MEASUREMENT_UNITS.width]})`}
            </Typography>
            <TextField
              id="lineWidth"
              variant="outlined"
              value={width}
              type="number"
              className="materials-input"
              InputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*',
                className: `${errors.width ? 'materials-input--error' : ''}`,
              }}
              fullWidth={true}
              onChange={(e) => {
                setWidth(e.target.value);
                clearErrors('width');
              }}
            />
          </label>
          {errors.width && <span className="materials-input--error-text">{errors.width}</span>}
        </Grid>
        {type !== 'eradication' && (
          <Grid item xs={6} lg={3}>
            <label htmlFor="lineRatio">
              <Typography variant="body1" color="textSecondary">
                {`Ratio ${MEASURE_UNITS_BY_SYSTEM[measurement][type === 'volume' ? MEASUREMENT_UNITS.lengthPerVolume : MEASUREMENT_UNITS.lengthPerWeight]}`}
              </Typography>
              <TextField
                id="lineRatio"
                value={ratio}
                type="number"
                variant="outlined"
                className="materials-input"
                InputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  className: `${errors.ratio ? 'materials-input--error' : ''}`,
                }}
                fullWidth={true}
                onChange={(e) => {
                  setRatio(e.target.value);
                  clearErrors('ratio');
                }}
              />
            </label>
            {errors.ratio && <span className="materials-input--error-text">{errors.ratio}</span>}
          </Grid>
        )}
        <Grid item xs={6} lg={3}>
          <label htmlFor="lineTestQuantity">
            <Typography variant="body1" color="textSecondary">
              {`Test Value ${MEASURE_UNITS_BY_SYSTEM[measurement][MEASUREMENT_UNITS.length]}`}
            </Typography>
            <TextField
              id="lineTestQuantity"
              value={testQuantity}
              variant="outlined"
              className="materials-input"
              type="number"
              InputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*',
                className: `${errors.testQuantity ? 'materials-input--error' : ''}`,
              }}
              fullWidth={true}
              onChange={(e) => setTestQuantity(e.target.value)}
            />
          </label>
          {errors.testQuantity && (
            <span className="materials-input--error-text">{errors.testQuantity}</span>
          )}
        </Grid>

        <Grid item xs={6} lg={3}>
          <label htmlFor="lineResult">
            <Typography variant="body1" color="textSecondary">
              {`Result ${MEASURE_UNITS_BY_SYSTEM[measurement][MEASUREMENT_UNITS[type === 'eradication' ? 'area' : type /** volume | weight */]]}`}
            </Typography>
            <TextField
              id="lineResult"
              value={lineResult}
              variant="outlined"
              InputProps={{
                type: 'number',
                inputMode: 'numeric',
                pattern: '[0-9]*',
              }}
              fullWidth={true}
              disabled={true}
            />
          </label>
        </Grid>

      </Grid>
      <Grid container spacing={1} style={{ justifyContent: 'flex-end' }}>
        {!Number.isInteger(editIndex) && (
          <Grid item xs={'auto'} style={{}}>
            <Button
              type="button"
              variant="outlined"
              size="medium"
              color="primary"
              style={{ padding: '2px 7px 2px 3px', marginTop: '15px' }}
              onClick={addLine}
              className={classes.addButton}
            >
              <AddIcon fontSize="small" color="primary" />
              <span>Add</span>
            </Button>
          </Grid>
        )}
        {!!Number.isInteger(editIndex) && (
          <>
            <Grid item xs={'auto'} style={{}}>
              <Button
                variant="outlined"
                size="medium"
                style={{ padding: '2px', marginTop: '15px' }}
                onClick={(e) => {
                  setEditIndex(null);
                  resetForm();
                }}
                className={classes.cancelEddingButton}
              >
                <span>Cancel</span>
              </Button>
            </Grid>
            <Grid item xs={'auto'} style={{}}>
              <Button
                variant="outlined"
                size="medium"
                color="primary"
                style={{ padding: '2px 10px', marginTop: '15px' }}
                onClick={editSave}
                className={classes.addButton}
              >
                <span>Save</span>
              </Button>
            </Grid>
          </>
        )}
      </Grid>
      {values?.length ? (
        <>
          <Grid container spacing={1} style={{ margin: '15px 0 0 0' }}>
            <Typography variant="body1" color="textSecondary">
              Click to edit
            </Typography>
          </Grid>
          <Grid container xs={12} style={{ marginTop: '7px' }}>
            <SortableList
              items={validatedValues}
              getId={getItemId}
              disabled={Number.isInteger(editIndex)}
              onMove={move}
              renderItem={(elem, index) => (
                <SpecificationChip
                  classes={classes}
                  elem={elem}
                  index={index}
                  editIndex={editIndex}
                  remove={remove}
                  typeOfLabel={'line'}
                  type={type}
                  measurement={measurement}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setErrors({});
                    setEditIndex(index);
                    setWidth(elem.width);
                    setRatio(elem.ratio);
                    setId(elem._id);
                  }}
                />
              )}
            />
          </Grid>
        </>
      ) : null}
    </Grid>
  );
};
export default withStyles(btnStyles)(LineSpecifications);
